import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

export default function TheMarket() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-8">
      <SlideHeader eyebrow="The ASO Market">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-5xl md:leading-[1.1]">
          €125M Target Enterprise Value in Month 24
          <SupLink to="ev-target" className="text-blue-700">G</SupLink>
        </h1>
        <div className="mt-4 flex flex-wrap items-stretch md:mt-5">
          <ChevronStep first>B2B SaaS</ChevronStep>
          <ChevronStep>MarTech</ChevronStep>
          <ChevronStep>Mobile Apps &amp; Games</ChevronStep>
          <ChevronStep>ASO</ChevronStep>
          <ChevronStep last terminal>
            Constant ASO Platform
          </ChevronStep>
        </div>
      </SlideHeader>

      {/* Body — two columns: Top Down + Bottom Up, each in its own box */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
        {/* TAM */}
        <div className="flex flex-col rounded-2xl bg-white p-5 ring-1 ring-slate-200 md:p-7">
          <p className="text-2xl font-bold tracking-tight text-blue-700 md:text-4xl">
            TAM
          </p>
          <p className="mt-1 text-xs font-medium uppercase tracking-[0.15em] text-slate-500 md:text-sm">
            Top Down
          </p>
          <div className="mt-4 flex flex-wrap items-start gap-4 md:mt-6 md:gap-6">
            <div>
              <p className="text-xs text-slate-600 md:text-sm">today</p>
              <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900 md:text-4xl">
                $0.5–1.0B
              </p>
            </div>
            <span className="pt-6 text-xl text-blue-500 md:pt-8 md:text-2xl">
              →
            </span>
            <div>
              <p className="text-xs text-slate-600 md:text-sm">by 2035</p>
              <p className="mt-1 text-2xl font-bold tracking-tight text-blue-700 md:text-4xl">
                $1.9–7.6B
              </p>
              <div className="mt-0.5 flex items-baseline justify-center gap-2 md:mt-1">
                <p className="text-base font-bold text-blue-700 md:text-xl">
                  12.5–20.7%
                </p>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 md:text-xs">
                  CAGR
                </p>
              </div>
            </div>
          </div>
          <p className="mt-auto pt-4 text-xs text-slate-600 md:pt-6 md:text-sm">
            Across four independent third-party reports of the ASO software
            category.<SupLink to="sources">8,9,10,11</SupLink> Scope<SupLink to="sources">12</SupLink>.
          </p>
        </div>

        {/* SAM */}
        <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200 md:p-7">
          <p className="text-2xl font-bold tracking-tight text-blue-700 md:text-4xl">
            SAM
          </p>
          <p className="mt-1 text-xs font-medium uppercase tracking-[0.15em] text-slate-500 md:text-sm">
            Bottom Up
          </p>
          <p className="mt-4 text-2xl font-bold tracking-tight text-blue-700 md:mt-6 md:text-4xl">
            €239M
          </p>
          <div className="mt-2 space-y-1 text-xs text-slate-600 md:mt-3 md:text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span>
                100K publishers (top decile by downloads) × €184/mo × 12
              </span>
              <span className="tabular-nums font-medium text-slate-900">
                €221M
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span>
                + ~3K agencies &amp; ASO professionals × €500/mo × 12
              </span>
              <span className="tabular-nums font-medium text-slate-900">
                €18M
              </span>
            </div>
          </div>
          <div className="mt-4 border-t border-slate-200 pt-3 md:mt-5 md:pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:mb-3">
              SOM rungs
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-slate-700">
                  0.7% · 700 accounts{" "}
                  <span className="text-slate-500">(m12 target)</span>
                </span>
                <span className="tabular-nums font-medium text-slate-900">
                  €1.55M
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-slate-700">5% · 5K accounts</span>
                <span className="tabular-nums font-medium text-slate-900">
                  €11M
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-slate-700">20% · 20K accounts</span>
                <span className="tabular-nums font-medium text-slate-900">
                  €44M
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom — 3 supporting pillars */}
      <div className="mt-5 grid grid-cols-1 gap-5 border-t border-slate-200 pt-5 md:mt-auto md:grid-cols-3 md:gap-10 md:pt-6">
        <Column label="ASO Professionals">
          <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
            5K+
          </p>
          <p className="text-sm text-slate-500">
            specialists &amp; agencies worldwide
          </p>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">Agencies</span>
              <span className="tabular-nums font-medium text-slate-900">
                ~400
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">Consultants</span>
              <span className="tabular-nums font-medium text-slate-900">
                ~3K
              </span>
            </div>
            <div className="flex items-baseline justify-between text-slate-500">
              <span>In-house</span>
              <span className="tabular-nums">~2K</span>
            </div>
          </div>
          <p className="mt-auto pt-3 text-xs italic text-slate-500">
            named anchors: Phiture, Gummicube, Yodel · source<SupLink to="sources">14</SupLink>
          </p>
        </Column>

        <Column label="App/Game Publishers">
          <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
            1M+
          </p>
          <p className="text-sm text-slate-500">
            active publishers globally (deduped)
          </p>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">App Store</span>
              <span className="tabular-nums font-medium text-slate-900">
                848K pub · 2.4M apps<SupLink to="sources">1</SupLink>
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">Google Play</span>
              <span className="tabular-nums font-medium text-slate-900">
                615K pub · 2.4M apps<SupLink to="sources">1</SupLink>
              </span>
            </div>
            <div className="flex items-baseline justify-between text-slate-500">
              <span>New apps / month</span>
              <span className="tabular-nums">≈110K total</span>
            </div>
          </div>
          <p className="mt-auto pt-3 text-xs italic text-slate-500">
            848K + 615K = 1.46M raw, less ≈30% cross-publishing overlap
          </p>
        </Column>

        <Column label="Category outcomes">
          <p className="mt-3 text-3xl font-bold tracking-tight text-blue-700">
            €1B+
          </p>
          <p className="text-sm text-slate-500">
            in M&amp;A + VC since 2021, across adjacent ASO/app-intel
          </p>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">Sensor Tower</span>
              <span className="tabular-nums font-medium text-slate-900">
                acq. data.ai (2024)
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">AppTweak</span>
              <span className="tabular-nums font-medium text-slate-900">
                Series B funded
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">AppFollow</span>
              <span className="tabular-nums font-medium text-slate-900">
                VC-backed
              </span>
            </div>
          </div>
          <p className="mt-auto pt-3 text-xs italic text-slate-500">
            source<SupLink to="sources">13</SupLink>
          </p>
        </Column>
      </div>
    </div>
  );
}

function Column({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
        {label}
      </p>
      {children}
    </div>
  );
}

function ChevronStep({
  children,
  first,
  last,
  terminal,
}: {
  children: ReactNode;
  first?: boolean;
  last?: boolean;
  terminal?: boolean;
}) {
  const padding = first
    ? "py-1.5 pl-3 pr-5 md:py-2.5 md:pl-5 md:pr-8"
    : last
    ? "py-1.5 pl-5 pr-3 md:py-2.5 md:pl-8 md:pr-5"
    : "py-1.5 pl-5 pr-5 md:py-2.5 md:pl-8 md:pr-8";

  // -ml is set to POINT - 2 so a 2px hairline gap reveals the chevron seam
  const overlap = first ? "" : "-ml-[8px] md:-ml-[12px]";

  const color = terminal
    ? "bg-blue-100 font-semibold text-blue-700"
    : "bg-slate-100 text-slate-700";

  // POINT = 10px (mobile), 14px (md). first = flat left + point right.
  // last = notch left + flat right. middle = notch left + point right.
  const clipMobile = first
    ? "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)]"
    : last
    ? "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,10px_50%)]"
    : "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%,10px_50%)]";

  const clipMd = first
    ? "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%)]"
    : last
    ? "md:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,14px_50%)]"
    : "md:[clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%,14px_50%)]";

  return (
    <div
      className={`flex items-center whitespace-nowrap text-sm md:text-lg ${padding} ${overlap} ${color} ${clipMobile} ${clipMd}`}
    >
      {children}
    </div>
  );
}

